/**
 * The home page's shelf (decision 77; part 11d log, table H): the order of My books, each book's cover colour, and when
 * a book was last written in, in words the page can show. Pure: the books, their writing, the clock and the zone come in.
 */
import type { BookWriting, ShelfSort } from './settings.ts';
import { localDay } from '../count/tally.ts';

/** H9: how many cover colours there are (`--cover-0`…`--cover-5` in `styles/tokens.css`, each with its `-ink`). */
export const COVER_COLOURS = 6;

/** A book on the shelf: its ID and the title it shows. */
export interface ShelfBook {
  readonly id: string;
  readonly title: string;
}

/**
 * H6: the books in `sort`'s order: last written in first, by title, or most words first. Books with nothing to order by
 * (never written in or counted on this device) follow, and ties keep, the book list's own order (the sort is stable).
 */
export function shelfOrder<B extends ShelfBook>(books: readonly B[], writing: (id: string) => BookWriting, sort: ShelfSort, locale: string): B[] {
  const most = (value: (book: B) => number | null) => (a: B, b: B): number => {
    const [x, y] = [value(a), value(b)];
    if (x === y) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return y - x;
  };
  switch (sort) {
    case 'edited':
      return [...books].sort(most((book) => writing(book.id).edited));
    case 'words':
      return [...books].sort(most((book) => writing(book.id).words));
    case 'title': {
      const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
      return [...books].sort((a, b) => collator.compare(a.title, b.title));
    }
  }
}

/** H9: a book's cover colour, 0 to {@link COVER_COLOURS} − 1: the same for the same ID, every time and on every device. */
export function coverOf(bookId: string): number {
  let sum = 0;
  for (let index = 0; index < bookId.length; index += 1) sum = (sum * 31 + bookId.charCodeAt(index)) % 9973;
  return sum % COVER_COLOURS;
}

/** H4: when a book was last written in, as the page says it: today or yesterday at a time, or on a day. */
export type Edited = { readonly kind: 'today' | 'yesterday'; readonly at: number } | { readonly kind: 'day'; readonly at: number };

/** H4: `at` against the device's day now (`zone`: minutes ahead of UTC at a time, as part 19b's D6). */
export function editedWhen(at: number, now: number, zone: (at: number) => number): Edited {
  const day = localDay(at, zone(at));
  const today = localDay(now, zone(now));
  if (day === today) return { kind: 'today', at };
  // The calendar day before today's, whatever the zone did between (a clock change makes a day 23 or 25 hours).
  const yesterday = new Date(Date.parse(today) - 86_400_000).toISOString().slice(0, 10);
  return day === yesterday ? { kind: 'yesterday', at } : { kind: 'day', at };
}
